/**
 * Claim Card Component
 * Displays extracted claims with source citations
 * Feature gated: HEATMAP flag controls entire Consensus feature
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Users } from 'lucide-react';
import { isSharedCitation, isValidUrl, truncateUrl } from '../../utils/citationUtils';

interface ClaimCitation {
  text: string;
  type: 'url' | 'reference' | 'doi';
  normalizedDomain?: string;
  position?: number;
  modelKey?: string; // Which model provided this citation
}

interface ClaimCardProps {
  claimText: string;
  claimType: 'fact' | 'opinion' | 'recommendation' | 'warning';
  citations: ClaimCitation[];
  allCitations?: ClaimCitation[]; // All citations across models for shared detection
  extractedBy?: string;
  showCitations?: boolean; // Whether to display citations section (default: true)
}

const MAX_VISIBLE_CITATIONS = 3;

export const ClaimCard: React.FC<ClaimCardProps> = ({
  claimText,
  claimType,
  citations,
  allCitations = [],
  extractedBy,
  showCitations = true
}) => {
  // 🔍 DIAGNOSTIC: Track where ClaimCard is being called from
  console.log('[🔍 DIAGNOSTIC] ClaimCard RENDERED', {
    claimPreview: claimText.substring(0, 50) + '...',
    citationsReceived: citations.length,
    allCitationsReceived: allCitations.length,
    extractedBy,
    callStack: new Error().stack?.split('\n').slice(1, 4).join('\n'),
    timestamp: new Date().toISOString()
  });

  const [citationsExpanded, setCitationsExpanded] = useState(false); // ✅ Collapsed by default
  const [showAllCitations, setShowAllCitations] = useState(false);

  // Filter to keep valid citations (URLs, references, and DOIs)
  const validCitations = useMemo(() => {
    return citations.filter(c =>
      (c.type === 'url' && isValidUrl(c.text)) ||
      c.type === 'reference' ||
      c.type === 'doi'
    );
  }, [citations]);

  // Log when citations prop changes
  React.useEffect(() => {
    console.log('[ClaimCard] Citations received', {
      claimText: claimText.substring(0, 50) + '...',
      citationsCount: citations.length,
      validCitationsCount: validCitations.length,
      hasCitations: validCitations.length > 0,
      citationsExpanded,
      sampleCitation: validCitations[0] ? {
        text: validCitations[0].text,
        type: validCitations[0].type,
        modelKey: validCitations[0].modelKey
      } : null
    });
  }, [citations, validCitations, claimText, citationsExpanded]);

  const hasCitations = validCitations && validCitations.length > 0;
  const hasManyCitations = validCitations.length > MAX_VISIBLE_CITATIONS;
  const displayedCitations = showAllCitations
    ? validCitations
    : validCitations.slice(0, MAX_VISIBLE_CITATIONS);

  // Claim type styling
  const typeStyles = {
    fact: 'border-blue-500/30 bg-blue-500/5',
    opinion: 'border-purple-500/30 bg-purple-500/5',
    recommendation: 'border-green-500/30 bg-green-500/5',
    warning: 'border-yellow-500/30 bg-yellow-500/5'
  };

  const typeBadgeStyles = {
    fact: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    opinion: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    recommendation: 'bg-green-500/20 text-green-300 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
  };

  const handleToggleCitations = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setCitationsExpanded(prev => !prev);
  };

  const handleShowAllCitations = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowAllCitations(true);
  };

  return (
    <div
      className={`w-full rounded-lg border p-4 ${typeStyles[claimType]} backdrop-blur-sm transition-all duration-200`}
    >
      {/* Claim type badge */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded border ${typeBadgeStyles[claimType]}`}
        >
          {claimType}
        </span>
        {extractedBy && (
          <span className="text-xs text-slate-400">
            via {extractedBy}
          </span>
        )}
      </div>

      {/* Claim text */}
      <p className="text-slate-200 mb-3 leading-relaxed">{claimText}</p>

      {/* Citations section (only shown if citations exist and showCitations is true) */}
          {showCitations && hasCitations && (
        <div className="border-t border-slate-700/50 pt-3">
          <button
            onClick={handleToggleCitations}
            className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            {citationsExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>
              Sources ({validCitations.length})
            </span>
          </button>

          {/* Expanded citations */}
          {citationsExpanded && (
            <div
              className="mt-3 space-y-2"
              onClick={event => event.stopPropagation()}
            >
              {displayedCitations.map((citation, idx) => {
                const sharedInfo = isSharedCitation(citation, allCitations);
                const isShared = sharedInfo.isShared;

                return (
                  <div
                    key={idx}
                    className={`flex items-start gap-2 p-2 rounded border ${
                      isShared
                        ? 'bg-cyan-500/10 border-cyan-500/30'
                        : 'bg-slate-800/30 border-slate-700/50'
                    }`}
                  >
                    <ExternalLink className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      isShared ? 'text-cyan-400' : 'text-slate-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      {citation.type === 'url' ? (
                        <a
                          href={citation.text}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={event => event.stopPropagation()}
                          className="text-sm text-cyan-400 hover:text-cyan-300 hover:underline break-all"
                          title={citation.text} // Show full URL on hover
                        >
                          {truncateUrl(citation.normalizedDomain || citation.text, 50)}
                        </a>
                      ) : (
                        <span className="text-sm text-slate-300">{citation.text}</span>
                      )}
                      <span className="text-xs text-slate-500 ml-2">
                        [{citation.type}]
                      </span>
                      {/* Shared citation badge */}
                      {isShared && (
                        <div className="flex items-center gap-1 mt-1">
                          <Users className="w-3 h-3 text-cyan-400" />
                          <span className="text-xs text-cyan-300 font-medium">
                            Cited by {sharedInfo.count} models
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Show more button for many citations */}
              {hasManyCitations && !showAllCitations && (
                <button
                  onClick={handleShowAllCitations}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors mt-2"
                >
                  Show {validCitations.length - MAX_VISIBLE_CITATIONS} more sources
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
